import React, { useCallback, useEffect, useState } from 'react';
import { getLoginAttempts, type LoginAttempt } from '../api/database';

type Props = {
  userEmail?: string;
  onLogout: () => void;
};

type DetailField = {
  label: string;
  value: string;
  multiline?: boolean;
};

const PAGE_SIZE = 50;

type AppliedFilters = {
  email: string;
  ipAddress: string;
  success: '' | 'true' | 'false';
  failureReason: string;
  start: string;
  end: string;
};

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function datetimeLocalToIso(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function displayValue(value: string | null | undefined): string {
  const text = String(value ?? '').trim();
  return text || '—';
}

function formatJson(value: unknown): string {
  if (value == null) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function parseUserAgentSummary(userAgent: string | null): string {
  if (!userAgent) return '—';
  try {
    const parsed = JSON.parse(userAgent) as {
      browser?: { name?: string; version?: string | null };
      os?: { name?: string; version?: string | null };
      device?: { type?: string };
    };
    const browser = [parsed.browser?.name, parsed.browser?.version].filter(Boolean).join(' ');
    const os = [parsed.os?.name, parsed.os?.version].filter(Boolean).join(' ');
    const device = parsed.device?.type;
    return [browser, os, device].filter(Boolean).join(' · ') || userAgent.slice(0, 80);
  } catch {
    return userAgent.split('\n')[0].slice(0, 80);
  }
}

function formatIpInfoSummary(ipInfo: LoginAttempt['ip_info']): string {
  if (!ipInfo) return '—';
  const parts = [
    ipInfo.city,
    ipInfo.region,
    ipInfo.country,
  ].filter((part) => typeof part === 'string' && part.trim());
  if (parts.length > 0) {
    return parts.join(', ');
  }
  if (ipInfo.private_ip) return 'Private / local IP';
  if (ipInfo.lookup_failed) return 'Lookup failed';
  return '—';
}

function buildDetailFields(row: LoginAttempt): DetailField[] {
  let userAgentFormatted = '—';
  if (row.user_agent) {
    try {
      userAgentFormatted = formatJson(JSON.parse(row.user_agent));
    } catch {
      userAgentFormatted = row.user_agent;
    }
  }

  return [
    { label: 'ID', value: displayValue(String(row.id)) },
    { label: 'Email', value: displayValue(row.email) },
    { label: 'IP address', value: displayValue(row.ip_address) },
    { label: 'Timestamp', value: formatTimestamp(row.created_at) },
    { label: 'Success', value: row.success ? 'Yes' : 'No' },
    { label: 'Failure reason', value: displayValue(row.failure_reason) },
    { label: 'Location / ISP', value: formatIpInfoSummary(row.ip_info) },
    { label: 'User agent', value: userAgentFormatted, multiline: true },
    { label: 'IP info', value: formatJson(row.ip_info), multiline: true },
  ];
}

const AdminLoginAttemptsPage: React.FC<Props> = ({ userEmail, onLogout }) => {
  const [attempts, setAttempts] = useState<LoginAttempt[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [emailFilter, setEmailFilter] = useState('');
  const [ipAddressFilter, setIpAddressFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState<'' | 'true' | 'false'>('');
  const [failureReasonFilter, setFailureReasonFilter] = useState('');
  const [startFilter, setStartFilter] = useState('');
  const [endFilter, setEndFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
    email: '',
    ipAddress: '',
    success: '',
    failureReason: '',
    start: '',
    end: '',
  });

  const loadAttempts = useCallback(async (nextOffset: number, append: boolean, filters: AppliedFilters) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setFetchError(null);

    try {
      const response = await getLoginAttempts({
        limit: PAGE_SIZE,
        offset: nextOffset,
        email: filters.email || undefined,
        ip_address: filters.ipAddress || undefined,
        success: filters.success === '' ? undefined : filters.success === 'true',
        failure_reason: filters.failureReason || undefined,
        start: datetimeLocalToIso(filters.start),
        end: datetimeLocalToIso(filters.end),
      });
      setTotal(response.total);
      setOffset(nextOffset);
      setAttempts((prev) => (append ? [...prev, ...response.attempts] : response.attempts));
    } catch (error) {
      setFetchError(error instanceof Error ? error.message : 'Failed to load login attempts');
      if (!append) {
        setAttempts([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadAttempts(0, false, appliedFilters);
  }, [appliedFilters, loadAttempts]);

  const handleApplyFilter = (event: React.FormEvent) => {
    event.preventDefault();
    setExpandedId(null);
    setAppliedFilters({
      email: emailFilter.trim(),
      ipAddress: ipAddressFilter.trim(),
      success: successFilter,
      failureReason: failureReasonFilter.trim(),
      start: startFilter.trim(),
      end: endFilter.trim(),
    });
  };

  const handleClearFilter = () => {
    setEmailFilter('');
    setIpAddressFilter('');
    setSuccessFilter('');
    setFailureReasonFilter('');
    setStartFilter('');
    setEndFilter('');
    setAppliedFilters({
      email: '',
      ipAddress: '',
      success: '',
      failureReason: '',
      start: '',
      end: '',
    });
    setExpandedId(null);
  };

  const handleRefresh = () => {
    setExpandedId(null);
    loadAttempts(0, false, appliedFilters);
  };

  const hasActiveFilters = Boolean(
    appliedFilters.email
    || appliedFilters.ipAddress
    || appliedFilters.success
    || appliedFilters.failureReason
    || appliedFilters.start
    || appliedFilters.end
  );

  const toggleExpanded = (rowId: string) => {
    setExpandedId((current) => (current === rowId ? null : rowId));
  };

  const hasMore = attempts.length < total;

  return (
    <div className="admin-errors-page">
      <header className="admin-errors-header">
        <div>
          <h1>Login Attempts</h1>
          <p className="admin-errors-subtitle">
            Security audit log for sign-in activity
            {userEmail ? ` · ${userEmail}` : ''}
          </p>
        </div>
        <button type="button" className="btn btn-login admin-errors-logout" onClick={onLogout}>
          Log out
        </button>
      </header>

      <section className="admin-errors-toolbar">
        <form className="admin-errors-filter login-attempts-filter" onSubmit={handleApplyFilter}>
          <input
            type="text"
            value={emailFilter}
            onChange={(event) => setEmailFilter(event.target.value)}
            placeholder="Filter by email"
            aria-label="Filter by email"
          />
          <input
            type="text"
            value={ipAddressFilter}
            onChange={(event) => setIpAddressFilter(event.target.value)}
            placeholder="Filter by IP address"
            aria-label="Filter by IP address"
          />
          <select
            value={successFilter}
            onChange={(event) => setSuccessFilter(event.target.value as AppliedFilters['success'])}
            aria-label="Filter by success status"
          >
            <option value="">All outcomes</option>
            <option value="true">Successful only</option>
            <option value="false">Failed only</option>
          </select>
          <input
            type="text"
            value={failureReasonFilter}
            onChange={(event) => setFailureReasonFilter(event.target.value)}
            placeholder="Filter by failure reason"
            aria-label="Filter by failure reason"
          />
          <label className="admin-errors-filter-field">
            <span>From</span>
            <input
              type="datetime-local"
              value={startFilter}
              onChange={(event) => setStartFilter(event.target.value)}
              aria-label="Show attempts from this timestamp onward"
            />
          </label>
          <label className="admin-errors-filter-field">
            <span>To</span>
            <input
              type="datetime-local"
              value={endFilter}
              onChange={(event) => setEndFilter(event.target.value)}
              aria-label="Show attempts up to this timestamp"
            />
          </label>
          <button type="submit" className="btn btn-login">
            Apply
          </button>
          <button
            type="button"
            className="btn admin-errors-refresh"
            disabled={loading}
            onClick={handleRefresh}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          {hasActiveFilters && (
            <button type="button" className="btn admin-errors-clear" onClick={handleClearFilter}>
              Clear
            </button>
          )}
        </form>
        <div className="admin-errors-count">
          {loading ? 'Loading…' : `${total} attempt${total === 1 ? '' : 's'}`}
        </div>
      </section>

      {fetchError && <div className="admin-errors-alert">{fetchError}</div>}

      {!loading && attempts.length === 0 && !fetchError && (
        <div className="admin-errors-empty">No login attempts found.</div>
      )}

      {attempts.length > 0 && (
        <div className="admin-errors-table-wrap">
          <table className="admin-errors-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Email</th>
                <th>IP</th>
                <th>Location</th>
                <th>Outcome</th>
                <th>Client</th>
                <th aria-label="Expand details" />
              </tr>
            </thead>
            <tbody>
              {attempts.map((row) => {
                const rowId = String(row.id);
                const isExpanded = expandedId === rowId;
                const detailFields = buildDetailFields(row);

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`admin-errors-row${isExpanded ? ' admin-errors-row-expanded' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                      onClick={() => toggleExpanded(rowId)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggleExpanded(rowId);
                        }
                      }}
                    >
                      <td>{formatTimestamp(row.created_at)}</td>
                      <td>{row.email}</td>
                      <td>{row.ip_address}</td>
                      <td>{formatIpInfoSummary(row.ip_info)}</td>
                      <td>
                        <span className={`login-attempt-outcome${row.success ? ' login-attempt-outcome-success' : ' login-attempt-outcome-failure'}`}>
                          {row.success ? 'Success' : displayValue(row.failure_reason)}
                        </span>
                      </td>
                      <td>{parseUserAgentSummary(row.user_agent)}</td>
                      <td className="admin-errors-actions">
                        <span className="admin-errors-expand-label">
                          {isExpanded ? 'Collapse' : 'Expand'}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="admin-errors-detail-row">
                        <td colSpan={7}>
                          <div className="admin-errors-detail-panel">
                            <div className="admin-errors-detail-grid">
                              {detailFields.map((field) => (
                                <div
                                  key={`${rowId}-${field.label}`}
                                  className={`admin-errors-detail-item${field.multiline ? ' admin-errors-detail-item-multiline' : ''}`}
                                >
                                  <div className="admin-errors-detail-label">{field.label}</div>
                                  <div className="admin-errors-detail-value">
                                    {field.multiline ? (
                                      <pre className="admin-errors-detail-pre">{field.value}</pre>
                                    ) : (
                                      field.value
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div className="admin-errors-load-more">
          <button
            type="button"
            className="btn btn-login"
            disabled={loadingMore}
            onClick={() => loadAttempts(offset + PAGE_SIZE, true, appliedFilters)}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminLoginAttemptsPage;
